import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import logoUrl from '../../assets/logos/zoniq.png';
import { useCart } from '../../context/CartContext';
import { useLocationContext, STATE_CITY_MAP, POPULAR_CITIES_MAP } from '../../context/LocationContext';
import { eventsApi } from '../../api';
import { EventItem } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { Icon } from '../icons/SpriteIcons';
import './shell.css';

const HOME_CATEGORIES = { pathname: '/', hash: '#home-categories' };
const HOME_CITIES = { pathname: '/', hash: '#home-cities' };

/** State + city chooser, shared by the desktop dropdown and the mobile drawer. */
const LocationPicker: React.FC<{ onCityChosen: () => void; maxHeightClass?: string }> = ({
  onCityChosen,
  maxHeightClass = 'max-h-[70vh]',
}) => {
  const { state, setState, city, setCity } = useLocationContext();
  const [showAll, setShowAll] = useState(false);
  const cities = showAll || !POPULAR_CITIES_MAP[state] ? STATE_CITY_MAP[state] : POPULAR_CITIES_MAP[state];
  const hasMore =
    !showAll && POPULAR_CITIES_MAP[state] && STATE_CITY_MAP[state]?.length > POPULAR_CITIES_MAP[state]?.length;

  return (
    <div className={`flex flex-col p-1.5 overflow-y-auto ${maxHeightClass}`}>
      <div className="px-3 py-2 text-[10px] font-medium tracking-widest text-white/40 uppercase mb-1">Select State</div>
      <div className="grid grid-cols-2 gap-1 mb-2">
        {Object.keys(STATE_CITY_MAP).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setState(s);
              setShowAll(false);
            }}
            className={`w-full text-left px-2 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 truncate ${
              state === s
                ? 'bg-primary/20 text-primary border border-primary/30'
                : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent'
            }`}
            title={s}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="w-full h-px bg-white/10 my-1" />

      <div className="px-3 py-2 text-[10px] font-medium tracking-widest text-white/40 uppercase mt-1 mb-1 flex justify-between items-center">
        <span>Select City</span>
        <span className="text-primary/70 truncate max-w-[80px]" title={state}>
          {state}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        {cities?.map((c: string) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              setCity(c);
              onCityChosen();
            }}
            className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium rounded-lg transition-all duration-200 ${
              city === c ? 'bg-primary/20 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'
            }`}
          >
            <span className="truncate">{c}</span>
            {city === c && <Icon name="i-check" size={16} />}
          </button>
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full text-center px-3 py-2 mt-1 text-xs font-medium text-primary/80 hover:text-primary hover:bg-white/5 rounded-lg transition-all duration-200"
          >
            Show All Cities
          </button>
        )}
      </div>
    </div>
  );
};

const SuggestionList: React.FC<{ query: string; suggestions: EventItem[]; onPick: () => void }> = ({
  query,
  suggestions,
  onPick,
}) => (
  <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-32px)] glass-panel hdr-dropdown rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in-up">
    {suggestions.length > 0 ? (
      <div className="flex flex-col p-1.5">
        <div className="px-3 py-2 text-[10px] font-medium tracking-widest text-white/40 uppercase mb-1">Suggestions</div>
        {suggestions.map((event) => (
          <Link
            key={event.id}
            to={`/events/${event.slug}`}
            onClick={onPick}
            className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-all duration-200 hover:bg-white/5 group"
          >
            {event.image ? (
              <img
                src={event.image}
                alt={event.title}
                loading="lazy"
                decoding="async"
                className="w-10 h-10 rounded object-cover border border-white/10 group-hover:border-primary/50 transition-colors"
              />
            ) : (
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded border border-white/10 bg-white/[0.04] text-white/30 transition-colors group-hover:border-primary/50">
                <Icon name="i-ticket" size={16} />
              </span>
            )}
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium text-white/90 truncate group-hover:text-primary transition-colors">
                {event.title}
              </p>
              <p className="text-xs text-white/50 truncate flex items-center gap-1">
                <span className="capitalize">{event.category}</span>
                <span className="w-1 h-1 rounded-full bg-white/20"></span>
                <span>{event.city}</span>
              </p>
            </div>
          </Link>
        ))}
      </div>
    ) : (
      <div className="p-6 text-center text-sm text-white/50">
        No results found for "<span className="text-white/80">{query}</span>"
      </div>
    )}
  </div>
);

export const Navbar: React.FC = () => {
  const { cart } = useCart();
  const { state, city } = useLocationContext();
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);
  const keepVisible = menuOpen || isSearchFocused || isLocationOpen || accountOpen;
  const keepVisibleRef = useRef(keepVisible);
  keepVisibleRef.current = keepVisible;

  // Hide on scroll down, reveal on scroll up (and always near the top).
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        if (y < 80 || keepVisibleRef.current) setHidden(false);
        else if (delta > 6) setHidden(true);
        else if (delta < -6) setHidden(false);
        if (Math.abs(delta) > 6 || y < 80) lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const locationRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getAllEvents(),
  });
  const events = useMemo(() => data || [], [data]);
  const fuse = useMemo(
    () => new Fuse(events, { keys: ['title', 'venue', 'city', 'category'], threshold: 0.3 }),
    [events]
  );
  const suggestions = useMemo(
    () => (searchQuery.trim() ? fuse.search(searchQuery).map((r) => r.item).slice(0, 5) : []),
    [searchQuery, fuse]
  );

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);
  const cityLabel = city === 'All Cities' && state !== 'All States' ? state : city;

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (locationRef.current && !locationRef.current.contains(target)) setIsLocationOpen(false);
      if (accountRef.current && !accountRef.current.contains(target)) setAccountOpen(false);
      if (searchRef.current && !searchRef.current.contains(target)) setIsSearchFocused(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // "/" jumps to search, like the prototype.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || /input|textarea|select/i.test((document.activeElement as HTMLElement | null)?.tagName || '')) {
        return;
      }
      const el = searchInputRef.current;
      if (el && el.offsetParent !== null) {
        e.preventDefault();
        el.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setIsLocationOpen(false);
    setIsSearchFocused(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setIsSearchFocused(false);
    setMenuOpen(false);
    navigate(`/events?search=${encodeURIComponent(q)}`);
  };

  const initials = user
    ? user.name
        .trim()
        .split(/\s+/)
        .map((n) => n[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
    : '';

  const handleSignOut = () => {
    logout();
    setAccountOpen(false);
    setMenuOpen(false);
    toast.success('Signed out');
    navigate('/');
  };

  return (
    // The drawer lives inside this sticky header, which is its own stacking
    // context (z-index:50 in shell.css). The fixed bottom bars - the event-detail
    // buy bar, checkout pay bar and the mobile tab bar - are portaled to <body>
    // at z-60/70, so without this they'd render on top of the open drawer and
    // cover its lower items. Lifting the whole header above them while the drawer
    // is open keeps the menu fully visible everywhere.
    <header
      className={`zq-shell hdr${hidden && !keepVisible ? ' hdr-hidden' : ''}`}
      style={menuOpen ? { zIndex: 100 } : undefined}
    >
      <div className="wrap">
        <div className="hdr-bar">
          <Link to="/" className="logo" aria-label="Zoniq home">
            <img src={logoUrl} alt="Zoniq" />
          </Link>

          <nav className="nav" aria-label="Primary">
            <NavLink to="/events">Discover</NavLink>
            <Link to={HOME_CATEGORIES}>Categories</Link>
            <Link to={HOME_CITIES}>Cities</Link>
            <NavLink to="/partners">For Partners</NavLink>
            <NavLink to="/about">About</NavLink>
          </nav>

          <div className="hdr-right">
            <div className="hdr-search-wrap" ref={searchRef}>
              <form className="hdr-search" role="search" onSubmit={handleSearch}>
                <Icon name="i-search" />
                <input
                  ref={searchInputRef}
                  type="search"
                  aria-label="Search events"
                  placeholder="Search events"
                  autoComplete="off"
                  value={searchQuery}
                  onFocus={() => setIsSearchFocused(true)}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearchFocused(true);
                  }}
                />
                <kbd>/</kbd>
              </form>
              {isSearchFocused && searchQuery.trim().length > 0 && (
                <SuggestionList
                  query={searchQuery}
                  suggestions={suggestions}
                  onPick={() => {
                    setIsSearchFocused(false);
                    setSearchQuery('');
                  }}
                />
              )}
            </div>
            <Link className="icon-btn hdr-search-icon" to="/events" aria-label="Search events">
              <Icon name="i-search" size={18} />
            </Link>

            <div className="relative hide-mobile" ref={locationRef}>
              <button
                type="button"
                className="hdr-pill"
                aria-haspopup="listbox"
                aria-expanded={isLocationOpen}
                aria-label="Select location"
                onClick={() => setIsLocationOpen((o) => !o)}
              >
                <Icon name="i-pin" size={14} />
                <span>{cityLabel}</span>
                <Icon name="i-chevron" size={14} />
              </button>
              {isLocationOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 top-full mt-2 w-64 glass-panel rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in-up"
                >
                  <LocationPicker onCityChosen={() => setIsLocationOpen(false)} maxHeightClass="max-h-[70vh]" />
                </div>
              )}
            </div>

            <Link
              to={isLoggedIn ? '/checkout' : '/auth'}
              state={isLoggedIn ? undefined : { from: { pathname: '/checkout' } }}
              className="icon-btn"
              aria-label={isLoggedIn ? `Cart${cartCount ? `, ${cartCount} items` : ''}` : 'Sign in to view your cart'}
            >
              <Icon name="i-bag" size={18} />
              {isLoggedIn && cartCount > 0 && <span className="badge">{cartCount}</span>}
            </Link>

            {isLoggedIn && user ? (
              <div className="account hide-mobile" ref={accountRef}>
                <button
                  type="button"
                  className="avatar"
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  aria-label="Account menu"
                  onClick={() => setAccountOpen((o) => !o)}
                >
                  {user.profilePic ? <img src={user.profilePic} alt="" /> : initials}
                </button>
                {accountOpen && (
                  <div className="account-menu glass-panel" role="menu">
                    <div className="account-head">
                      <b>{user.name}</b>
                      <span>{user.email}</span>
                    </div>
                    <Link role="menuitem" to="/profile" onClick={() => setAccountOpen(false)}>
                      Profile
                    </Link>
                    <Link role="menuitem" to="/my-tickets" onClick={() => setAccountOpen(false)}>
                      My tickets
                    </Link>
                    {user.role === 'admin' && (
                      <Link role="menuitem" to="/dashboard" onClick={() => setAccountOpen(false)}>
                        Dashboard
                      </Link>
                    )}
                    <button type="button" role="menuitem" className="signout" onClick={handleSignOut}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link className="btn btn-ghost btn-sm hide-mobile" to="/auth">
                Sign in
              </Link>
            )}
            <a className="btn btn-primary btn-sm hide-mobile" href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">
              Partner with us
            </a>
            <button
              type="button"
              className="icon-btn menu-btn"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <Icon name="i-menu" size={20} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <>
        <div className="drawer-backdrop" aria-hidden onClick={() => setMenuOpen(false)} />
        <div className="drawer" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="drawer-top">
            <Link to="/" className="logo" aria-label="Zoniq home">
              <img src={logoUrl} alt="Zoniq" />
            </Link>
            <button type="button" className="icon-btn" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
              <Icon name="i-x" size={20} />
            </button>
          </div>
          <div>
            <nav aria-label="Mobile">
              <Link to="/events">Discover events</Link>
              <Link to={HOME_CATEGORIES}>Categories</Link>
              <Link to={HOME_CITIES}>Cities</Link>
              <Link to="/partners">For partners</Link>
              <Link to="/about">About Zoniq</Link>
              {isLoggedIn && <Link to="/my-tickets">My tickets</Link>}
              {isLoggedIn && user?.role === 'admin' && <Link to="/dashboard">Dashboard</Link>}
            </nav>

            <form className="drawer-search" role="search" onSubmit={handleSearch}>
              <Icon name="i-search" size={18} />
              <input
                type="search"
                aria-label="Search events"
                placeholder="Search events"
                autoComplete="off"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>

            <div className="drawer-block">
              <div className="glass-panel rounded-xl overflow-hidden">
                <div className="px-4 pt-3 text-[10px] font-medium tracking-widest text-white/40 uppercase">
                  Your city · <span className="text-primary">{cityLabel}</span>
                </div>
                <LocationPicker onCityChosen={() => setMenuOpen(false)} maxHeightClass="max-h-[26vh]" />
              </div>
            </div>

            <div className="drawer-foot">
              <a className="btn btn-primary btn-lg" href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">
                Partner with Zoniq
              </a>
              {isLoggedIn ? (
                <>
                  <Link className="btn btn-ghost btn-lg" to="/profile">
                    Your profile
                  </Link>
                  <button type="button" className="btn btn-ghost btn-lg" onClick={handleSignOut}>
                    Sign out
                  </button>
                </>
              ) : (
                <Link className="btn btn-ghost btn-lg" to="/auth">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
        </>
      )}
    </header>
  );
};
